import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, ViewContainerRef, Renderer2, ComponentFactoryResolver } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { ApiError, AuthService, BaseVisualizacaoService, ConfigService, DataTablesColumnSettings, DataTablesService, MessageService, Task, TaskService, VisualizacaoStorageService } from '../../../../core';
import { DecimalFormatPipe } from '../../../../shared';
import { ConfigEmpresa, isConfigEmpresaVersaoIntegracaoErp1 } from '../../../configuracoes/app/empresa';
import { IntegrationRepresentanteHttpService } from '../../../configuracoes/integration/representante';
import { RepresentanteFilter } from '../filter';
import { RepresentanteSearchRepresentante, RepresentanteSearchTotal } from '../representante-search';
import { RepresentanteSearchOrderBy } from '../representante-search-orderby';
import { RepresentanteTableOptions } from '../representante-table-options';
import { RepresentanteCrudHttpService, RepresentanteSearchHttpService } from '../services';
import { RepresentanteSituacaoBadgePipe, RepresentanteSituacaoPipe } from '../situacao';
import { RepresentanteModalidadeComercialBadgeComponent } from '../components/modalidade-comercial/badge';

@Component({
    selector: 'sgt-representante-list',
    templateUrl: './representante-list.component.html',
    styleUrls: ['./representante-list.component.less']
})
export class RepresentanteListComponent {
    private searchSubs$: Subscription;
    configEmpresa: ConfigEmpresa;
    isIntegracaoErpV1: boolean;
    isAdministrador: boolean;
    isAtendente: boolean;

    idDataTable = 'tableRepresentantes';
    dataTable: any;
    total: RepresentanteSearchTotal;

    private readonly visualizacaoStorageKey = 'representanteListVisualizacao';
    @Input() filter = new RepresentanteFilter();
    orderBy: number;
    @Output() orderByChanged = new EventEmitter<number>();
    orderByDir: string;
    @Output() orderByDirChanged = new EventEmitter<string>();

    /** Container para criar dinamicamente os badges das modalidades comerciais */
    @ViewChild('badgeModalidadeComercialContainer', {read: ViewContainerRef}) badgeModalidadeComercialContainer: ViewContainerRef;

    constructor(private activatedRoute: ActivatedRoute,
                private el: ElementRef,
                private componentFactoryResolver: ComponentFactoryResolver,
                private renderer: Renderer2,
                private authService: AuthService,
                private baseVisualizacaoService: BaseVisualizacaoService,
                private configService: ConfigService,
                private messageService: MessageService,
                private dataTablesService: DataTablesService,
                private taskService: TaskService,
                private visualizacaoStorageService: VisualizacaoStorageService,
                private decimalFormatPipe: DecimalFormatPipe,
                private representanteSituacaoPipe: RepresentanteSituacaoPipe,
                private representanteSituacaoBadgePipe: RepresentanteSituacaoBadgePipe,
                private representanteSearchHttpService: RepresentanteSearchHttpService,
                private representanteCrudHttpService: RepresentanteCrudHttpService,
                private integrationRepresentanteHttpService: IntegrationRepresentanteHttpService) { }

    ngOnInit() {
        this.configEmpresa = this.configService.getEmpresa();
        this.isIntegracaoErpV1 = isConfigEmpresaVersaoIntegracaoErp1(this.configEmpresa);
        this.isAdministrador = this.authService.isAdministrador();
        this.isAtendente = this.authService.isAtendente();
    }

    search() {
        this.searchSubs$?.unsubscribe();
        this.searchSubs$ = new Subscription();

        if (!this.dataTable) {
            // Obtendo os dados armazenados da visualização
            const vs = this.visualizacaoStorageService.get();
            // Criando a dataTable assíncrona
            this.createTable().then(() => {
                // Se possuir dados armazenados, então aplicamos eles na dataTable
                if (vs != null) {
                    this.dataTable.search(vs.filterSearch)
                                    .page(vs.currentPage)
                                    .order(vs.order)
                                    .draw(false);
                }
            });
        } else {
            this.dataTable.ajax.reload();
        }
    }

    /** Atualizando a pesquisa */
    refresh() {
        this.dataTable?.draw(false);
    }

    /** Cria a Datatables assíncrona */
    createTable(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const columns: DataTablesColumnSettings[] = [
                {
                    data: null,
                    className: 'col-options',
                    orderable: false,
                    render: (data, type, row, meta) => {
                        const entity = data as RepresentanteSearchRepresentante;
                        let template = '';

                        // Opções
                        const tableOptions = new RepresentanteTableOptions();
                        // Edit
                        tableOptions.candEdit = true;
                        // Delete
                        tableOptions.canDelete = true;
                        // Import One ERP v1
                        tableOptions.canImportOneErp_v1 = this.canImportOneErp_v1(entity);
                        if (tableOptions.hasAnyOption()) {
                            template += `
                                <span class="btn-group dropup table-options">
                                    <button type="button" title="Opções" class="btn btn-link btn-options dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                        <i class="fas fa-ellipsis-h" aria-hidden="true"></i>
                                    </button>
                                    <div class="dropdown-menu">
                                `;
                            // Edit
                            if (tableOptions.candEdit) {
                                template += `
                                    <a class="dropdown-item" id="edit">
                                        <i class="fas fa-pencil-alt" aria-hidden="true"></i> Editar
                                    </a>
                                `;
                            }
                            // Delete
                            if (tableOptions.canDelete) {
                                template += `
                                    <a class="dropdown-item dropdown-item-delete" id="delete">
                                        <i class="far fa-trash-alt" aria-hidden="true"></i> Excluir
                                    </a>
                                `;
                            }
                             // Import One ERP v1
                             if (tableOptions.canImportOneErp_v1) {
                                template += `
                                    <div class="dropdown-divider"></div>
                                    <a class="dropdown-item" id="importOneErp_v1">
                                        <i class="fas fa-download"></i> Importar do ERP
                                    </a>
                                `;
                             }
                             template += `
                                    </div>
                                </span>
                            `;
                        }

                        return template;
                    },
                    createdCell: (td, cellData) => {
                        const entity = cellData as RepresentanteSearchRepresentante;
                        const myself = this;

                        const dropdown = jQuery(td).find('.btn-group');
                        // Ao abrir o dropdown, adicionamos ele no body para exibi-lo corretamente na table
                        dropdown.on('show.bs.dropdown', function() {
                            jQuery('body').append(dropdown.css({
                                    position: 'absolute',
                                    left: dropdown.offset().left,
                                    top: dropdown.offset().top
                                }).detach());
                        });
                        // Ai fechar o dropdown, adicionamos ele novamente a coluna das opções na table
                        dropdown.on('hidden.bs.dropdown', function() {
                            jQuery(td).append(dropdown.css({
                                position: 'relative',
                                left: 'auto',
                                top: 'auto'
                            }).detach());
                        });
                        // Opções do dropdown
                        dropdown.on('click', 'a', function() {
                            if (this.id === 'edit') {
                                myself.edit(entity);
                            } else if (this.id === 'delete') {
                                myself.delete(entity);
                            } else if (this.id === 'importOneErp_v1') {
                                myself.importOneErp_v1(entity);
                            }
                        });
                    }
                },
                {
                    data: 'codigoIntegracaoErp',
                    className: 'col-codigo-integracao-erp',
                    serverOrderByColumn: RepresentanteSearchOrderBy.CodigoIntegracaoErp
                },
                {
                    data: 'nome',
                    className: 'col-nome',
                    serverOrderByColumn: RepresentanteSearchOrderBy.Nome,
                    createdCell: (td, cellData, rowData, row, col) => {
                        if (cellData) {
                            td.title = cellData;
                        }
                    }
                },
                {
                    data: 'nomeFantasia',
                    className: 'col-nome-fantasia',
                    serverOrderByColumn: RepresentanteSearchOrderBy.NomeFantasia,
                    createdCell: (td, cellData, rowData, row, col) => {
                        if (cellData) {
                            td.title = cellData;
                        }
                    }
                },
                {
                    data: 'cnpjCpf',
                    className: 'col-cnpj-cpf',
                    serverOrderByColumn: RepresentanteSearchOrderBy.CnpjCpf
                },
                {
                    data: 'situacao',
                    className: 'col-situacao text-center',
                    serverOrderByColumn: RepresentanteSearchOrderBy.Situacao,
                    render: (data: number) => `<span class="${this.representanteSituacaoBadgePipe.transform(data)}">${this.representanteSituacaoPipe.transform(data)}</span>`
                },
                {
                    data: null,
                    className: 'col-modalidade-comercial text-center',
                    serverOrderByColumn: RepresentanteSearchOrderBy.ModalidadeComercial,
                    render: () => '',
                    createdCell: (td: any, cellData: any, entity: RepresentanteSearchRepresentante) => {
                        if (entity) {
                            const factory = this.componentFactoryResolver.resolveComponentFactory(RepresentanteModalidadeComercialBadgeComponent);
                            const ref = this.badgeModalidadeComercialContainer.createComponent(factory);
                            ref.instance.modalidade = entity.modalidadeComercial;
                            this.renderer.appendChild(td, ref.location.nativeElement);
                        }
                    }
                },
                // Prospects
                {
                    data: null,
                    className: 'col-quantidade-prospects text-center',
                    serverOrderByColumn: RepresentanteSearchOrderBy.QuantidadeProspects,
                    render: (data: RepresentanteSearchRepresentante) => this.decimalFormatPipe.transform(data?.quantidadeProspects, 1, 0, 0)
                },
                // Clientes
                {
                    data: null,
                    className: 'col-quantidade-clientes text-center',
                    serverOrderByColumn: RepresentanteSearchOrderBy.QuantidadeClientes,
                    render: (data: RepresentanteSearchRepresentante) => this.decimalFormatPipe.transform(data?.quantidadeClientes, 1, 0, 0)
                },
                {
                    data: null,
                    className: 'col-quantidade-clientes-novos text-center',
                    serverOrderByColumn: RepresentanteSearchOrderBy.QuantidadeClientesNovos,
                    render: (data: RepresentanteSearchRepresentante) => this.decimalFormatPipe.transform(data?.quantidadeClientesNovos, 1, 0, 0)
                },
                {
                    data: null,
                    className: 'col-quantidade-clientes-positivos text-center',
                    serverOrderByColumn: RepresentanteSearchOrderBy.QuantidadeClientesPositivos,
                    render: (data: RepresentanteSearchRepresentante) => this.decimalFormatPipe.transform(data?.quantidadeClientesPositivos, 1, 0, 0)
                },
                {
                    data: null,
                    className: 'col-quantidade-clientes-ativos text-center',
                    serverOrderByColumn: RepresentanteSearchOrderBy.QuantidadeClientesAtivos,
                    render: (data: RepresentanteSearchRepresentante) => this.decimalFormatPipe.transform(data?.quantidadeClientesAtivos, 1, 0, 0)
                },
                {
                    data: null,
                    className: 'col-quantidade-clientes-preinativos text-center',
                    serverOrderByColumn: RepresentanteSearchOrderBy.QuantidadeClientesPreInativos,
                    render: (data: RepresentanteSearchRepresentante) => this.decimalFormatPipe.transform(data?.quantidadeClientesPreInativos, 1, 0, 0)
                },
                {
                    data: null,
                    className: 'col-quantidade-clientes-inativos text-center',
                    serverOrderByColumn: RepresentanteSearchOrderBy.QuantidadeClientesInativos,
                    render: (data: RepresentanteSearchRepresentante) => this.decimalFormatPipe.transform(data?.quantidadeClientesInativos, 1, 0, 0)
                },
                {
                    data: null,
                    className: 'col-quantidade-clientes-congelados text-center',
                    serverOrderByColumn: RepresentanteSearchOrderBy.QuantidadeClientesCongelados,
                    render: (data: RepresentanteSearchRepresentante) => this.decimalFormatPipe.transform(data?.quantidadeClientesCongelados, 1, 0, 0)
                },
                {
                    data: null,
                    className: 'col-quantidade-clientes-bloqueados text-center',
                    serverOrderByColumn: RepresentanteSearchOrderBy.QuantidadeClientesBloqueados,
                    render: (data: RepresentanteSearchRepresentante) => this.decimalFormatPipe.transform(data?.quantidadeClientesBloqueados, 1, 0, 0)
                }
            ];

            this.dataTablesService.showTable(`#${this.idDataTable}`, this.dataTable, {
                columns,
                order: [[2, 'asc']], // nome asc
                searching: false,
                info: false,
                serverSide: true,
                processing: true,
                deferRender: true,
                scroller: true,
                scrollX: true,
                ajax: (data, callback, settings) => { // Evento Ajax para pesquisar os representante
                     // OrderBy
                     const order = data?.order[0];
                     const orderColumn = columns[order?.column];
                     this.orderBy = orderColumn?.serverOrderByColumn;
                     this.orderByChanged.emit(this.orderBy);
                     this.orderByDir = order?.dir;
                     this.orderByDirChanged.emit(this.orderByDir);

                    this.searchSubs$.add(this.representanteSearchHttpService.search(this.filter, this.orderBy, this.orderByDir, data.length, data.start)
                        .subscribe(
                            response => {
                                if (response) {
                                    this.total = response.total
                                    callback({recordsTotal: response.total.quantidadeRepresentantes, recordsFiltered: response.total.quantidadeRepresentantes, data: response.representantes});
                                } else {
                                    callback({recordsTotal: 0, recordsFiltered: 0, data: []});
                                }
                            },
                            (error: ApiError) => {
                                this.messageService.error(error.message, 'Erro ao pesquisar os representantes', error);
                                callback({recordsTotal: 0, recordsFiltered: 0, data: []});
                            }
                        )
                    );
                }
            })
            .then(dataTable => {
                this.dataTable = dataTable;
                this.dataTable
                    .on('preDraw', () => { // Antes de redesenhar a table
                        //
                    });

                    setTimeout(() => {
                        // Adicionando evento handler no scroll do body da datatable
                        jQuery(this.el.nativeElement).find('.dataTables_scrollBody').on('scroll', () => {
                            this.visualizacaoStorageService.set(null, null, this.dataTable?.page?.info()?.page, this.dataTable?.order(), this.dataTable?.scroller?.page()?.start, this.visualizacaoStorageKey);
                        });

                        resolve();
                    }, 500);
            });
        });
    }

    private edit(entity: RepresentanteSearchRepresentante) {
        this.baseVisualizacaoService.edit(entity.id, this.activatedRoute);
    }

    private delete(entity: RepresentanteSearchRepresentante) {
        if (!entity) {
            return;
        }
        const nomeRepresentante = `${entity.codigoIntegracaoErp} - ${entity.nome}`;
        this.messageService.confirm(`Deseja realmente excluir o representante ${nomeRepresentante}?`, 'Confirme').then(result => {
            if (result) {
                this.representanteCrudHttpService.delete(entity.id, false)
                    .subscribe(
                        () => {
                            this.messageService.success('', `Representante ${nomeRepresentante} excluído com sucesso`);
                            this.search();
                        },
                        (error: ApiError) => this.messageService.error(error.message, 'Erro ao excluir o representante', error)
                    );
            }
        });
    }

    /** Verifica se pode importar do ERP V1 */
    canImportOneErp_v1(entity: RepresentanteSearchRepresentante): boolean {
        if (!entity) {
            return false;
        }
        // Somente se for a versão 1
        if (!this.isIntegracaoErpV1) {
            return false;
        }
        // Se o cliente ainda não possui o código de integração, então não pode importar do ERP
        if (!!entity.codigoIntegracaoErp) {
            return false;
        }
        // Se o representante ainda não estiver integrado, então não pode importar do ERP
        if (!entity.integradoErp) {
            return false;
        }

        return this.authService.podeImportarErp();
    }

    /** Executa a integração de leitura individual dos dados do representante no ERP */
    importOneErp_v1(entity: RepresentanteSearchRepresentante) {
        if (!entity) {
            return;
        }

        const task = new Task(`Importando representante ${entity.codigoIntegracaoErp}`, `Executando importação do representante ${entity.codigoIntegracaoErp} do sistema ERP`);
        this.taskService.add(task, task.description);
        this.integrationRepresentanteHttpService.execReadIndividualIntegration(entity.id)
            .subscribe(
                () => {
                    this.taskService.success(task, `Importação do representante ${entity.codigoIntegracaoErp} do sistema ERP realizada com sucesso`);
                    this.refresh();
                },
                (error: ApiError) => this.taskService.error(task, `Erro ao realizar a importação do representante ${entity.codigoIntegracaoErp} do sistema ERP`, error.message, error)
            );
    }

    ngOnDestroy(): void {
        // Realizando unsubscribe dos subscriptions das pesquisas
        this.searchSubs$?.unsubscribe();
        // Destruindo badges das modalidades comerciais
        this.badgeModalidadeComercialContainer?.clear();

        // Removendo evento de scroll do body da datatable
        jQuery(this.el.nativeElement).find('.dataTables_scrollBody').off('scroll');
    }
}
